import React, { useMemo, forwardRef } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import {
  Heading,
  Paragraph,
  Block,
  RadioButtonGroup,
  RadioButton,
  Text,
  ExternalLink,
  Checkbox,
  CheckboxGroup,
  SingleSelect,
  SingleSelectData,
  StatusText,
} from 'suomifi-ui-components'
import {
  validateTodistusLanguage,
  validateTranslationFormLanguage,
} from 'todistukset-common/src/vtj/form-validation.util'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import TranslationFormWithPreviousAddressesInlineAlert from 'todistukset-ui/src/vtj/components/order-wizard/TranslationFormWithPreviousAddressesInlineAlert.react'
import {
  getPropertyTranslationKey,
  getValidationErrorFromValidator,
} from 'todistukset-ui/src/vtj/components/order-wizard/validation'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import {
  VtjOteTodistusType,
  isVtjOteTodistus,
} from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import { useFirstErrorImperativeHandle } from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import fp from 'lodash/fp'
import { TodistusStoreForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { FormContainer } from 'todistukset-ui/src/vtj/components/order-wizard/AdditionalSelectionsStep.sc'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { checkIfTranslationIsAvailable } from 'todistukset-ui/src/vtj/translation.util'
import { checkIfApostilleIsAvailable } from 'todistukset-ui/src/vtj/apostille.util'
import getFormActions from 'todistukset-ui/src/vtj/stores/actions/get-form-actions'
import translateLanguage from 'common-translations/src/vtj/translate-language'
import {
  getNativeLanguagesByCountryCode,
  availableTranslations,
} from 'todistukset-common/src/vtj/language.util'
import {
  editIlmoitettuArvo,
  finalEditableFieldValue,
  KaannosKieli,
} from 'todistukset-common/src/vtj/types/editable-field.type'
import { toTodistusKaannosKieli } from 'todistukset-common/src/vtj/types/todistus-kaannos-kieli.type'

const AdditionalSelectionsStep: StepComponent = observer(
  forwardRef(function AdditionalSelectionsStep(
    { titleId, certificateType },
    ref
  ) {
    if (!isVtjOteTodistus(certificateType)) {
      throw new Error(
        `Type check failed. This step supports only VtjOteTodistusType but ${certificateType} was given.`
      )
    }

    const {
      forms: { [certificateType]: form },
    } = useStore()
    const { todistusLanguage, usageRegion } = form
    const { t } = useTranslation()

    const todistusLanguageValue = finalEditableFieldValue(todistusLanguage)
    const refs = useFirstErrorImperativeHandle(ref, form, getErrors, [
      'todistusLanguage',
      'translationFormLanguage',
    ])

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="orderWizardAdditionalSelectionsStepMainTitle" />
          </Heading>
        </Block>
        <Block variant="section">
          <Heading variant="h3">
            <LocalizedString stringKey="orderWizardAdditonalSelectionsStepLanguageSelectionTitle" />
          </Heading>
          {checkIfTranslationIsAvailable({
            usageRegion,
            todistusLanguage: LanguageCode.FI,
          }) && (
            <Paragraph>
              <LocalizedString stringKey="orderWizardAdditonalSelectionsStepLanguageSelectionText" />
            </Paragraph>
          )}
          <CertificateLanguageSelection
            certificateType={certificateType}
            ref={refs.todistusLanguage}
          />
        </Block>

        {checkIfApostilleIsAvailable({ usageRegion }) && (
          <Block variant="section" data-test-id="apostille-section">
            <Heading variant="h3">
              <LocalizedString stringKey="orderWizardAdditonalSelectionsStepApostilleSelectionTitle" />
            </Heading>
            <Paragraph>
              <LocalizedString stringKey="orderWizardAdditonalSelectionsStepApostilleSelectionText" />
            </Paragraph>
            <Paragraph>
              <ExternalLink
                href={t(
                  'orderWizardAdditonalSelectionsStepApostilleSelectionTextLinkHref'
                )}
                labelNewWindow={t('opensInNewWindow')}
                underline="initial"
              >
                <LocalizedString stringKey="orderWizardAdditonalSelectionsStepApostilleSelectionLinkText" />
              </ExternalLink>
            </Paragraph>
            <ApostilleSelection certificateType={certificateType} />
          </Block>
        )}
        {checkIfTranslationIsAvailable({
          usageRegion,
          todistusLanguage: todistusLanguageValue,
        }) && (
          <Block variant="section" data-test-id="translation-section">
            <Heading variant="h3">
              <LocalizedString stringKey="orderWizardAdditonalSelectionsStepTranslationSelectionTitle" />
            </Heading>
            <Paragraph>
              <LocalizedString stringKey="orderWizardAdditonalSelectionsStepTranslationSelectionText" />
            </Paragraph>
            <TranslationLanguageSelection
              certificateType={certificateType}
              ref={refs.translationFormLanguage}
            />
            <TranslationFormWithPreviousAddressesInlineAlert
              certificateType={certificateType}
            />
          </Block>
        )}
      </FormContainer>
    )
  })
)

const CertificateLanguageSelection: React.FC<
  {
    certificateType: VtjOteTodistusType
  } & React.RefAttributes<HTMLInputElement>
> = observer(
  forwardRef(function CertificateLanguageSelection({ certificateType }, ref) {
    const {
      common: { lang },
      forms: { [certificateType]: form },
      steps: {
        [certificateType]: { showStepValidationErrors },
      },
    } = useStore()
    const { t } = useTranslation()
    const { todistusLanguage } = form
    const todistusLanguageValue = finalEditableFieldValue(todistusLanguage)
    const { changeCertificationLanguage } = getFormActions(certificateType)

    const error = getErrors(form).todistusLanguage

    const availableLanguages = [
      LanguageCode.FI,
      LanguageCode.SV,
      LanguageCode.EN,
    ]

    return (
      <>
        <RadioButtonGroup
          id="option-cert-language"
          name="language-radio"
          labelText={
            <LocalizedString stringKey="orderWizardAdditonalSelectionsStepLanguageSelectionTitle" />
          }
          labelMode="hidden"
          onChange={(value) => {
            changeCertificationLanguage(value as LanguageCode)
          }}
          value={todistusLanguageValue ?? undefined}
        >
          {availableLanguages.map((selectableLanguage, index) => {
            return (
              <RadioButton
                key={selectableLanguage}
                value={selectableLanguage}
                ref={index === 0 ? ref : undefined}
              >
                <Text>
                  {translateLanguage({ code: selectableLanguage, lang })}
                </Text>
              </RadioButton>
            )
          })}
        </RadioButtonGroup>
        {showStepValidationErrors && error ? (
          <StatusText status="error">
            {t(getPropertyTranslationKey(error))}
          </StatusText>
        ) : null}
      </>
    )
  })
)

const ApostilleSelection: React.FC<{ certificateType: VtjOteTodistusType }> =
  observer(({ certificateType }) => {
    const {
      forms: {
        [certificateType]: { includeApostille },
      },
    } = useStore()
    const { toggleIncludeApostille } = getFormActions(certificateType)
    const { t } = useTranslation()

    return (
      <CheckboxGroup
        labelText={
          <LocalizedString stringKey="orderWizardAdditonalSelectionsStepApostilleSelectionCheckboxTitle" />
        }
        optionalText={t('inputOptional')}
      >
        {[
          <Checkbox
            key="certificate-apostille-form-checkbox"
            checked={includeApostille.ilmoitettuArvo ?? false}
            onClick={() => toggleIncludeApostille()}
            data-test-id="certificate-apostille-form-checkbox"
          >
            <Text>
              <LocalizedString stringKey="orderWizardAdditonalSelectionsStepApostilleSelectionCheckboxLabel" />
            </Text>
          </Checkbox>,
        ]}
      </CheckboxGroup>
    )
  })

const getSingleSelectDataMapper =
  (userLanguage: LanguageCode) => (languageCode: LanguageCode) => ({
    labelText: translateLanguage({ code: languageCode, lang: userLanguage }),
    uniqueItemId: languageCode as string,
  })

const getSortedSelectOptions = ({
  lang = LanguageCode.FI,
  usageRegion,
}: {
  lang?: LanguageCode
  usageRegion: TodistusStoreForm['usageRegion']
}): SingleSelectData[] => {
  const convertLanguageCodeToSingleSelectData = getSingleSelectDataMapper(lang)

  const nativeLanguages = getNativeLanguagesByCountryCode(
    usageRegion.countryKoodistoCode
  )

  const primaryOptions = nativeLanguages.map(
    convertLanguageCodeToSingleSelectData
  ) satisfies SingleSelectData[]

  const secondaryOptions: SingleSelectData[] = fp.flow([
    fp.difference(availableTranslations),
    fp.map(convertLanguageCodeToSingleSelectData),
    fp.sortBy('label'),
  ])(nativeLanguages)

  return [...primaryOptions, ...secondaryOptions]
}

const TranslationLanguageSelection: React.FC<
  {
    certificateType: VtjOteTodistusType
  } & React.RefAttributes<HTMLInputElement>
> = observer(
  forwardRef(function TranslationLanguageSelection({ certificateType }, ref) {
    const {
      common: { lang },
      forms: { [certificateType]: form },
      steps: {
        [certificateType]: { showStepValidationErrors },
      },
    } = useStore()
    const { usageRegion, includeTranslationForm, translationFormLanguage } =
      form

    const translationFormLanguageValue = finalEditableFieldValue(
      translationFormLanguage
    )

    const { t } = useTranslation()
    const { setTranslationFormLanguage, toggleIncludeTranslationForm } =
      getFormActions(certificateType)

    const languageItems = useMemo(
      () => getSortedSelectOptions({ lang, usageRegion }),
      [lang, usageRegion]
    )

    const error = getErrors(form).translationFormLanguage

    return (
      <>
        <CheckboxGroup
          labelText={
            <LocalizedString stringKey="orderWizardAdditonalSelectionsStepTranslationSelectionCheckboxTitle" />
          }
          optionalText={t('inputOptional')}
        >
          {[
            <Checkbox
              key="certificate-translation-form-checkbox"
              checked={includeTranslationForm.ilmoitettuArvo ?? false}
              onClick={() => toggleIncludeTranslationForm()}
              data-test-id="certificate-translation-form-checkbox"
            >
              <Text>
                <LocalizedString stringKey="orderWizardAdditonalSelectionsStepTranslationSelectionCheckboxLabel" />
              </Text>
            </Checkbox>,
          ]}
        </CheckboxGroup>

        <SingleSelect
          id="select-translation-language-widget"
          key={`translation-form-${lang}`}
          data-test-id="certificate-translation-form-dropdown"
          labelText={
            <LocalizedString stringKey="orderWizardAdditonalSelectionsStepTranslationSelectionLanguageSelectLabel" />
          }
          visualPlaceholder={t(
            'orderWizardAdditonalSelectionsStepTranslationSelectionLanguageSelectLabel'
          )}
          clearButtonLabel={t('orderWizardSingleSelectClearSelection')}
          noItemsText={t('orderWizardSingleSelectNoMatchingItems') ?? ''}
          ariaOptionsAvailableTextFunction={(count) =>
            t('orderWizardSingleSelectFilteredResultsAvailable', {
              count,
            })
          }
          disabled={!includeTranslationForm.ilmoitettuArvo}
          items={languageItems}
          selectedItem={
            translationFormLanguageValue
              ? getSingleSelectDataMapper(lang)(translationFormLanguageValue)
              : null
          }
          onItemSelectionChange={(item) =>
            setTranslationFormLanguage(
              editIlmoitettuArvo<KaannosKieli>(
                null,
                item?.uniqueItemId
                  ? toTodistusKaannosKieli(item.uniqueItemId)
                  : null
              )
            )
          }
          status={showStepValidationErrors && error ? 'error' : 'default'}
          statusText={
            showStepValidationErrors && error
              ? t(getPropertyTranslationKey(error))
              : undefined
          }
          ref={ref}
        />
      </>
    )
  })
)

function getErrors(form: TodistusStoreForm) {
  return {
    todistusLanguage: getValidationErrorFromValidator(
      validateTodistusLanguage,
      finalEditableFieldValue(form.todistusLanguage),
      form,
      'todistusLanguage'
    ),
    translationFormLanguage: getValidationErrorFromValidator(
      validateTranslationFormLanguage,
      finalEditableFieldValue(form.translationFormLanguage),
      form,
      'translationFormLanguage'
    ),
  }
}

export default AdditionalSelectionsStep
